// «Хроника» — комикс-карусель по ПАНЕЛЯМ главы (картинка + свой блок текста; листаешь —
// меняется и картинка, и текст) + краткая выжимка дня перед каруселью + полный ридер по
// «Открыть главу». Панели приходят с бэка (chapter.panels), картинка каждой резолвится в
// живой MediaAsset. Для пролога / отдельных циклов / старых глав без панелей — прежнее
// поведение (recapImages). Изолированная .gn-* разметка.

import { useEffect, useMemo, useState } from "react";
import { api, type CampaignSetup, type MediaAsset, type WorldChange } from "../api";
import { buildChronicleShowcaseItems, compactChangeLabel } from "../cycle/shared";
import {
  chapterLiteraryText,
  chronicleDecisionStatus,
  hasChapterLiteraryText,
  worldChangeDescription,
} from "../cycle/ChronicleSection";
import { useAssetContent } from "../cycle/NegotiationRooms";
import { splitNarrativeParagraphs } from "../text";

type ShowcaseItem = ReturnType<typeof buildChronicleShowcaseItems>[number];

const SECTION_LABEL: Record<string, string> = {
  SITUATION: "Ситуация",
  ACTION: "Действие",
  INITIATIVE: "Инициатива",
  OUTCOME: "Итог дня",
};

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 6l-6 6 6 6" />
    </svg>
  );
}
function FwdIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

function ComicPanel({ campaignId, asset, index, hero }: { campaignId: string; asset: MediaAsset; index: number; hero?: boolean }) {
  const src = useAssetContent(campaignId, asset);
  if (asset.status === "READY" && src) {
    return <img className={hero ? "gn-chero-img" : "gn-cth-img"} src={src} alt="" />;
  }
  if (asset.status === "FAILED") {
    return (
      <div className="gn-cth-gen">
        Иллюстрация
        <br />
        не создана
      </div>
    );
  }
  return (
    <div className="gn-cth-gen">
      Готовится
      <br />
      кадр {index + 1}
    </div>
  );
}

function WorldChip({ change }: { change: WorldChange }) {
  const tone = change.delta > 0 ? "pos" : change.delta < 0 ? "neg" : "";
  return (
    <span className={`gn-wchip ${tone}`}>
      <b>{compactChangeLabel(change.displayName)}</b>
      <span className="ar">{change.delta > 0 ? "↑" : change.delta < 0 ? "↓" : "·"}</span>
      <i>
        {change.before} → {change.after}
      </i>
    </span>
  );
}

function ChronicleReader({
  campaignId,
  item,
  onClose,
}: {
  campaignId: string;
  item: ShowcaseItem;
  onClose: () => void;
}) {
  const [page, setPage] = useState(0);
  const feed = item.feedItem;
  const panelImages =
    feed.kind === "chapter"
      ? feed.chapter.panels.map((p) => p.image).filter((x): x is MediaAsset => !!x)
      : [];
  const images = panelImages.length > 0 ? panelImages : item.images;
  const heroAsset = images[Math.min(page, images.length - 1)];

  return (
    <div className="gn-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="gn-modal gn-reader" onClick={(e) => e.stopPropagation()}>
        <div className="gn-modal-head">
          <div className="gn-tt">
            <b>{item.meta} · {item.title}</b>
            <span>Хроника</span>
          </div>
          <button type="button" className="gn-modal-x" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>

        {images.length > 0 && (
          <div className="gn-comic">
            <div className="gn-chero">
              {heroAsset && <ComicPanel campaignId={campaignId} asset={heroAsset} index={page} hero />}
              {images.length > 1 && (
                <span className="gn-cpg">
                  {page + 1} / {images.length}
                </span>
              )}
            </div>
            {images.length > 1 && (
              <div className="gn-cthumbs gn-pstrip">
                {images.map((a, i) => (
                  <button
                    key={a.id}
                    type="button"
                    className={`gn-cth${i === page ? " on" : ""}`}
                    onClick={() => setPage(i)}
                  >
                    <ComicPanel campaignId={campaignId} asset={a} index={i} />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {feed.kind === "chapter" ? (
          <>
            <div className="gn-msect">С чего всё началось</div>
            <p className="gn-mtext">{feed.chapter.openingSummary || item.situationText}</p>

            {feed.chapter.keyEvents.filter((e) => e.eventType !== "DAY_OUTCOME" && hasChapterLiteraryText(e)).length > 0 && (
              <>
                <div className="gn-msect">Ключевые события</div>
                {feed.chapter.keyEvents
                  .filter((e) => e.eventType !== "DAY_OUTCOME" && hasChapterLiteraryText(e))
                  .map((e) => (
                    <p className="gn-mtext" key={e.id}>
                      <b>{e.title}. </b>
                      {chapterLiteraryText(e)}
                    </p>
                  ))}
              </>
            )}

            {feed.chapter.decisions.length > 0 && (
              <>
                <div className="gn-msect">Решения Совета</div>
                {feed.chapter.decisions.map((d) => (
                  <div className="gn-drow" key={d.initiativeId}>
                    <b>{d.title}</b>
                    <span className={/(REJECT|NOT_FUND)/i.test(d.status) ? "no" : ""}>
                      {chronicleDecisionStatus(d.status)}
                    </span>
                  </div>
                ))}
              </>
            )}

            {feed.chapter.worldChanges.length > 0 && (
              <>
                <div className="gn-msect">Что изменилось</div>
                {feed.chapter.worldChanges.map((c, i) => (
                  <div className="gn-drow" key={i}>
                    <b>{c.displayName}</b>
                    <span className={c.delta < 0 ? "no" : ""}>{worldChangeDescription(c)}</span>
                  </div>
                ))}
              </>
            )}

            <div className="gn-msect">Итог дня</div>
            <p className="gn-mtext">{feed.chapter.closingNarrative || item.outcomeText}</p>
          </>
        ) : (
          <>
            <div className="gn-msect">{item.situationHeading}</div>
            {splitNarrativeParagraphs(("body" in feed ? feed.body : "") || item.situationText).map((para, i) => (
              <p className="gn-mtext" key={i}>
                {para}
              </p>
            ))}
          </>
        )}
        <div style={{ height: "10px" }} />
      </div>
    </div>
  );
}

export function ChronicleScreen({ campaignId, setup }: { campaignId: string; setup: CampaignSetup }) {
  const cycle = setup.cycle.current;
  const items = useMemo(() => (cycle ? buildChronicleShowcaseItems(setup, cycle) : []), [setup, cycle]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [panelPage, setPanelPage] = useState(0);
  const [openItem, setOpenItem] = useState<ShowcaseItem | null>(null);

  const unreadCount = cycle?.unread?.chronicleCount ?? 0;
  useEffect(() => {
    if (unreadCount > 0) void api.markRead(campaignId, "CHRONICLE").catch(() => undefined);
  }, [campaignId, cycle?.id, unreadCount]);

  if (!cycle) return null;
  if (items.length === 0) {
    return (
      <div className="gn-body">
        <div className="gn-chead">
          <span className="gn-eyebrow">Хроника кампании</span>
          <h2>Комикс по главам</h2>
        </div>
        <div className="gn-card gn-empty">
          Комикс ещё не сформирован. Сначала появится пролог, затем главы по итогам каждого дня.
        </div>
      </div>
    );
  }

  const idx = activeIndex == null ? items.length - 1 : Math.min(Math.max(0, activeIndex), items.length - 1);
  const active = items[idx];
  const chapter = active.feedItem.kind === "chapter" ? active.feedItem.chapter : null;
  const panels = chapter?.panels ?? [];
  const usePanels = panels.length > 0;
  const panelIdx = Math.min(Math.max(0, panelPage), Math.max(0, panels.length - 1));
  const panel = panels[panelIdx];

  const heroReady = active.images.find((a) => a.status === "READY");
  const readyCount = active.images.filter((a) => a.status === "READY").length;
  const isPrologue = active.feedItem.kind === "prologue";
  const legacySummary = "summary" in active.feedItem ? active.feedItem.summary : active.summaryText;
  const digest = chapter?.summary?.trim() || legacySummary;

  const goChapter = (i: number) => {
    setActiveIndex(i);
    setPanelPage(0);
  };

  return (
    <div className="gn-body">
      <div className="gn-chead">
        <span className="gn-eyebrow">Хроника кампании</span>
        <h2>Комикс по главам</h2>
      </div>

      <div className="gn-cnav">
        <button type="button" className="gn-arw" disabled={idx === 0} onClick={() => goChapter(Math.max(0, idx - 1))}>
          <BackIcon />
        </button>
        <div className="gn-dots">
          {items.map((it, i) => (
            <button key={it.id} type="button" className={`gn-cdot${i === idx ? " on" : ""}`} onClick={() => goChapter(i)}>
              <small>{it.meta}</small>
              <strong>{it.shortLabel}</strong>
            </button>
          ))}
        </div>
        <button
          type="button"
          className="gn-arw"
          disabled={idx === items.length - 1}
          onClick={() => goChapter(Math.min(items.length - 1, idx + 1))}
        >
          <FwdIcon />
        </button>
      </div>

      <div className="gn-card gn-cstage">
        <div className="gn-meta">{active.meta}</div>
        <h3>{active.title}</h3>
        {digest && (
          <div className="gn-sum">
            {splitNarrativeParagraphs(digest).map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        )}

        {usePanels && panel ? (
          <>
            <div className="gn-comic">
              <div className="gn-chero">
                {panel.image ? (
                  <ComicPanel campaignId={campaignId} asset={panel.image} index={panelIdx} hero />
                ) : (
                  <div className="gn-cth-gen">
                    Иллюстрация
                    <br />
                    готовится
                  </div>
                )}
                {panels.length > 1 && (
                  <span className="gn-cpg">
                    {panelIdx + 1} / {panels.length}
                  </span>
                )}
              </div>
              {panels.length > 1 && (
                <div className="gn-pnav">
                  <button type="button" className="gn-arw" disabled={panelIdx === 0} onClick={() => setPanelPage(panelIdx - 1)}>
                    <BackIcon />
                  </button>
                  <span className="gn-plabel">
                    {SECTION_LABEL[panel.section] ?? "Сцена"} · {panelIdx + 1}/{panels.length}
                  </span>
                  <button
                    type="button"
                    className="gn-arw"
                    disabled={panelIdx === panels.length - 1}
                    onClick={() => setPanelPage(panelIdx + 1)}
                  >
                    <FwdIcon />
                  </button>
                </div>
              )}
            </div>

            <div className="gn-wpanel">
              <h4>{panel.heading}</h4>
              {splitNarrativeParagraphs(panel.text).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
              {panel.section === "OUTCOME" && panel.worldChanges.length > 0 && (
                <div className="gn-wgrid">
                  {panel.worldChanges.slice(0, 6).map((c, i) => (
                    <WorldChip change={c} key={i} />
                  ))}
                </div>
              )}
            </div>

            {panels.length > 1 && (
              <div className="gn-cthumbs gn-pstrip">
                {panels.map((p, i) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`gn-cth${i === panelIdx ? " on" : ""}`}
                    onClick={() => setPanelPage(i)}
                  >
                    {p.image ? <ComicPanel campaignId={campaignId} asset={p.image} index={i} /> : <div className="gn-cth-gen">{i + 1}</div>}
                  </button>
                ))}
              </div>
            )}

            <button type="button" className="gn-cta" onClick={() => setOpenItem(active)}>
              Открыть главу
            </button>
          </>
        ) : (
          <>
            <div className="gn-comic">
              <button type="button" className="gn-chero" onClick={() => setOpenItem(active)}>
                {heroReady ? (
                  <ComicPanel campaignId={campaignId} asset={heroReady} index={0} hero />
                ) : (
                  <div className="gn-cth-gen">Комикс ещё формируется</div>
                )}
                {readyCount > 1 && <span className="gn-cpg">1 / {readyCount}</span>}
              </button>
              {active.images.length > 1 && (
                <div className="gn-cthumbs">
                  {active.images.slice(0, 4).map((a, i) => (
                    <button key={a.id} type="button" className="gn-cth" onClick={() => setOpenItem(active)}>
                      <ComicPanel campaignId={campaignId} asset={a} index={i} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="gn-wpanel">
              <h4>{active.situationHeading}</h4>
              {splitNarrativeParagraphs(active.situationText).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            {!isPrologue && (
              <div className="gn-wpanel">
                <h4>{active.outcomeHeading}</h4>
                {splitNarrativeParagraphs(active.outcomeText).map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
                {active.changes.length > 0 && (
                  <div className="gn-wgrid">
                    {active.changes.slice(0, 6).map((c, i) => (
                      <WorldChip change={c} key={i} />
                    ))}
                  </div>
                )}
              </div>
            )}

            <button type="button" className="gn-cta" onClick={() => setOpenItem(active)}>
              Открыть главу
            </button>
          </>
        )}
      </div>

      {openItem && <ChronicleReader campaignId={campaignId} item={openItem} onClose={() => setOpenItem(null)} />}
    </div>
  );
}
